import { LucideCheckCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { WhiteBox } from '../blocks/WhiteBox';
import { PageBody, PageHeading } from '../layout/AppLayout';
import { h2ClassName, h2WhiteBoxClassName } from '../components/ui/styles';
import CountrySelect from '../components/ProxyPoolCreationForm/CountrySelect';
import CitySelect from '../components/ProxyPoolCreationForm/CitySelect';
import NameInput from '../components/ProxyPoolCreationForm/NameInput';
import ProxyPoolTypeSelector from '../components/ProxyPoolCreationForm/ProxyPoolTypeSelector';
import useProxyPoolCreationForm from '../store/proxyPoolCreationFormStore';
import { fetchCreateProxyPool } from '@fd/common/api/createPool';
import ProxyAmountInput from '../components/ProxyPoolCreationForm/ProxyAmountInput';
import { fetchGenerateProxies } from '@fd/common/api/fetchGenerateProxies';
import RotationSelect from '../components/ProxyPoolCreationForm/RotationSelect';
import { ProxyTypeZodEnum } from '@fd/common/biz/proxyType';
import { countryByCode } from '../model/static_countries';
import static_cities from '../model/static_cities';
import { ProxyPoolRotationSchema } from '@fd/common/biz/proxyPoolRotationOptions';
import ProtocolSelect from '../components/ProxyPoolCreationForm/ProtocolSelect';
import { useTariffByProxyType } from '../hooks/useTariffByProxyType';
import { useTrafficGbAvailableByProxyType } from '../hooks/useTrafficAvailableByProxyType';
import { HardButton } from '../components/ui/HardButton';
import { useSession } from '../context/session/useSession';

export function ProxyPoolCreatePage() {
  const {
    country,
    setCountry,
    proxyType,
    setProxyType,
    name,
    cities,
    setCities,
    quantity,
    rotation,
    setRotation,
    protocol,
    resetForm,
    isCreateAfterPayment,
    setCreateAfterPayment,
  } = useProxyPoolCreationForm();
  const tariff = useTariffByProxyType(proxyType);
  const trafficAvailable = useTrafficGbAvailableByProxyType(proxyType);
  const navigate = useNavigate();
  const { session } = useSession();

  const payment = useSearchParams()[0].get('payment');
  const searchParams = useSearchParams()[0];
  const isAfterPaymentSuccess = 'success' === payment;

  const [isCreating, setCreating] = useState(false);

  const createProxyPool = useCallback(() => {
    const asyncCreateProxyPool = async () => {
      if (!tariff.meter_subscription_id) {
        throw Error('handleCreateProxyPool no subscription id found');
      }
      const body = {
        subscription_id: tariff.meter_subscription_id,
        countries_list: [country.countryCode],
        pool_name: name,
        cities_list: cities,
        rotation,
        protocol,
      };
      console.log('newPool body', body);
      const newPool = await fetchCreateProxyPool(body);
      console.log('newPool response', newPool);
      const proxyPoolUrl = `/proxy-pool/${newPool.id}`;
      await fetchGenerateProxies(newPool.id, quantity);
      resetForm();
      navigate(proxyPoolUrl);
    };
    asyncCreateProxyPool();
  }, [
    cities,
    country.countryCode,
    name,
    navigate,
    protocol,
    quantity,
    resetForm,
    rotation,
    tariff.meter_subscription_id,
  ]);

  useEffect(() => {
    const parsedType = ProxyTypeZodEnum.safeParse(searchParams.get('type'));
    if (parsedType.success) setProxyType(parsedType.data);

    const countryParam = searchParams.get('country');
    if (countryParam && countryByCode.has(countryParam)) {
      setCountry(countryByCode.get(countryParam)!);
      const citiesParam = searchParams.get('cities')?.split(',') || [];
      const parsedCities = [];
      for (const city of citiesParam) {
        if (
          static_cities[countryParam as keyof typeof static_cities].find(
            (c) => c.value == city
          )
        ) {
          parsedCities.push(city);
        }
      }
      setCities(parsedCities);
    }

    const rotationParam = searchParams.get('rotation');
    if (rotationParam) {
      const parsedRotation = ProxyPoolRotationSchema.safeParse(
        parseInt(rotationParam)
      );
      if (parsedRotation.success) setRotation(parsedRotation.data);
    }
  }, [searchParams, setCities, setCountry, setProxyType, setRotation]);

  useEffect(() => {
    if (isAfterPaymentSuccess && isCreateAfterPayment) {
      createProxyPool();
    }
  }, [isCreateAfterPayment, isAfterPaymentSuccess, createProxyPool]);

  const handleCreateProxyPool = async () => {
    if (trafficAvailable === 0 || !session) {
      setCreateAfterPayment(true);
      navigate(`/proxies/buy?type=${proxyType}`);
      return;
    }

    setCreating(true);
    try {
      await createProxyPool();
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <PageHeading>Create new proxy pool</PageHeading>
      <PageBody>
        <>
          {' '}
          {isAfterPaymentSuccess && (
            <div className="border-indigo-500 bg-indigo-100 rounded-lg p-4 mb-7">
              <h2 className={'text-indigo-800 text-lg font-semibold'}>
                <LucideCheckCircle className="inline-block mr-3 " />
                Payment successful
              </h2>
            </div>
          )}
          <WhiteBox>
            <h2 className={h2WhiteBoxClassName}>Type</h2>
            <div className="mt-2 mb-4">
              <ProxyPoolTypeSelector />
            </div>
            <NameInput />
            <div className="mb-4">
              <h2 className={h2ClassName}>Locations</h2>
              <div className="flex flex-col gap-2">
                <CountrySelect />
                <CitySelect />
              </div>
            </div>
            <div className="mb-4">
              <h2 className={h2ClassName}>Rotation</h2>
              <div className="flex flex-col gap-2">
                <RotationSelect />
              </div>
            </div>
            <div className="mb-4">
              <h2 className={h2ClassName}>Protocol</h2>
              <div className="flex flex-col gap-2">
                <ProtocolSelect />
              </div>
            </div>
            <ProxyAmountInput />
            <div>
              <HardButton
                id="create-proxy-pool-button"
                onClick={handleCreateProxyPool}
                disabled={isCreating}
              >
                {trafficAvailable && trafficAvailable > 0 ? (
                  <>Create new proxy pool</>
                ) : (
                  <>Buy proxy and create proxy pool</>
                )}
              </HardButton>
            </div>
          </WhiteBox>
        </>
      </PageBody>
    </>
  );
}
